import React from 'react';
import {
  Chip,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import { BAG_PRESET_OPTIONS, DEFAULT_BAG_PRESET } from '../../features/scrabble/config/gameConfig';
import { toNumber, formatInt, formatDateTime } from '../../utils/format';
import WidgetShell from './WidgetShell';

const BAG_PRESET_LABEL_BY_ID = BAG_PRESET_OPTIONS.reduce((acc, option) => {
  acc[option.id] = option.label;
  return acc;
}, {});

const toBagPresetLabel = (bagPresetId) => {
  const normalizedId = String(bagPresetId || '').toLowerCase().trim();
  if (!normalizedId) return 'No Bag Data';
  if (BAG_PRESET_LABEL_BY_ID[normalizedId]) return BAG_PRESET_LABEL_BY_ID[normalizedId];
  if (normalizedId === DEFAULT_BAG_PRESET) return BAG_PRESET_LABEL_BY_ID[DEFAULT_BAG_PRESET] || 'No Bag Data';
  return 'No Bag Data';
};

function RunsWidget({ stats }) {
  const [runsView, setRunsView] = React.useState('recent');

  const recentRuns = Array.isArray(stats?.history?.recentRuns)
    ? stats.history.recentRuns.slice(0, 8)
    : [];

  const bestRuns = React.useMemo(() => {
    if (!Array.isArray(stats?.history?.recentRuns)) return [];
    return [...stats.history.recentRuns]
      .sort(
        (a, b) =>
          toNumber(b?.finalScore ?? b?.totalScore ?? b?.score) -
          toNumber(a?.finalScore ?? a?.totalScore ?? a?.score)
      )
      .slice(0, 8);
  }, [stats?.history?.recentRuns]);

  const displayedRuns = runsView === 'recent' ? recentRuns : bestRuns;

  return (
    <WidgetShell
      icon={<ReceiptLongRoundedIcon color="primary" fontSize="small" />}
      title="Runs"
      action={
        <ToggleButtonGroup
          value={runsView}
          exclusive
          onChange={(_, val) => {
            if (val) setRunsView(val);
          }}
          size="small"
        >
          <ToggleButton value="recent" sx={{ px: 1.5, py: 0.4, fontSize: 12, fontWeight: 700 }}>
            Recent
          </ToggleButton>
          <ToggleButton value="best" sx={{ px: 1.5, py: 0.4, fontSize: 12, fontWeight: 700 }}>
            Best
          </ToggleButton>
        </ToggleButtonGroup>
      }
    >
      {displayedRuns.length ? (
        <Stack divider={<Divider flexItem />}>
          {displayedRuns.map((run, index) => {
            const bagPresetId = run?.bagPreset || run?.bagPresetId || run?.preset || run?.bagType || '';
            const roundReachedValue = run?.roundReached ?? run?.roundIndex;
            const runScoreValue = run?.finalScore ?? run?.totalScore ?? run?.score;
            const hasRoundData = Number.isFinite(Number(roundReachedValue));
            const hasScoreData = Number.isFinite(Number(runScoreValue));

            return (
              <Stack
                key={`${run.timestamp || index}-${index}`}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ py: 1 }}
              >
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <Chip size="small" label={toBagPresetLabel(bagPresetId)} variant="outlined" />
                    {run.result === 'won' && (
                      <Chip
                        size="small"
                        label="Won"
                        color="warning"
                        sx={{ fontWeight: 800, fontSize: 10 }}
                      />
                    )}
                    <Typography sx={{ fontWeight: 700 }}>
                      {hasRoundData ? `Round ${formatInt(roundReachedValue)}` : 'No Data'}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(run.timestamp)}
                  </Typography>
                </Stack>
                <Typography sx={{ fontWeight: 800 }}>
                  {hasScoreData ? `${formatInt(runScoreValue)} pts` : 'No Data'}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <Typography color="text.secondary">No run history yet.</Typography>
      )}
    </WidgetShell>
  );
}

export default RunsWidget;
